import clsx from "clsx";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AppShell } from "@/components/AppShell";
import { ProjectOverviewLink } from "@/components/ProjectOverviewLink";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { CollapsibleSection } from "@/components/ui/CollapsibleSection";
import { SelectInput, TextInput } from "@/components/ui/Field";
import { Spinner } from "@/components/ui/Spinner";
import { Table, type Column } from "@/components/ui/Table";
import { ApiError } from "@/lib/apiClient";
import { useCurrentUser, userIsBuero } from "@/lib/auth";
import {
  formatDateShortDe,
  formatDateTimeDe,
  formatEuroCompact,
  formatEuroDe,
  OPEN_INVOICE_BUCKET_LABELS,
  OPEN_INVOICE_BUCKETS,
  type OpenInvoiceBucket,
  type OpenInvoiceCustomer,
} from "@/lib/heroDocumentReports";
import {
  activateHeroDokument,
  getDokument,
  ZAHLUNGSSTATUS_LABELS,
  type DokumentDetail,
} from "@/lib/dokumente";
import { MAHNSTUFE_LABELS } from "@/lib/mahnwesen";
import {
  fetchOffenePosten,
  type OffenePostenReport,
  type OffenerPostenItem,
} from "@/lib/offenePosten";
import { toast } from "@/lib/toast";
import { DocumentPaymentsDialog } from "@/pages/dokumente/DocumentPaymentsDialog";
import { useDocumentEditor } from "@/pages/dokumente/useDocumentEditor";
import "@/pages/auswertungen/auswertungen.css";

type DetailView = "invoices" | "customers";
type DueFilter = "alle" | "ueberfaellig" | "nicht_faellig" | "ohne_faelligkeit" | "teilbezahlt";

const DUE_FILTERS: Array<{ value: DueFilter; label: string }> = [
  { value: "alle", label: "Alle offenen" },
  { value: "ueberfaellig", label: "Überfällig" },
  { value: "nicht_faellig", label: "Noch nicht fällig" },
  { value: "ohne_faelligkeit", label: "Ohne Fälligkeitsdatum" },
  { value: "teilbezahlt", label: "Teilbezahlt" },
];

function canCaptureOffenerPostenZahlung(row: OffenerPostenItem): boolean {
  if ((row.summe_offen ?? 0) <= 0) return false;
  if ((row.dokument_id ?? 0) > 0) return true;
  return (row.hero_document_id ?? 0) > 0;
}

function overdueClass(bucket: OpenInvoiceBucket): string {
  if (bucket === "d61_plus") return "eh-docreport-overdue--severe";
  if (bucket === "d31_60") return "eh-docreport-overdue--high";
  if (bucket === "d1_30") return "eh-docreport-overdue--mild";
  return "";
}

function AgingBar({
  buckets,
  total,
}: {
  buckets: OffenePostenReport["summary"]["buckets"];
  total: number;
}) {
  if (total <= 0) {
    return <p className="text-sm text-label-secondary">Keine offenen Rechnungen.</p>;
  }
  return (
    <div className="space-y-3">
      <div
        className="flex h-8 w-full overflow-hidden rounded-lg ring-1 ring-separator/40"
        role="img"
        aria-label="Offene Beträge nach Fälligkeit"
      >
        {OPEN_INVOICE_BUCKETS.map((bucket) => {
          const amount = buckets[bucket]?.amount ?? 0;
          if (amount <= 0) return null;
          return (
            <div
              key={bucket}
              className={clsx(
                "eh-docreport-aging__segment",
                `eh-docreport-aging__segment--${bucket}`,
              )}
              style={{ width: `${(amount / total) * 100}%` }}
              title={`${OPEN_INVOICE_BUCKET_LABELS[bucket]}: ${formatEuroDe(amount)}`}
            />
          );
        })}
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-2">
        {OPEN_INVOICE_BUCKETS.map((bucket) => (
          <div key={bucket} className="flex items-center gap-2 text-xs text-label-secondary">
            <span
              className={clsx(
                "inline-block h-2.5 w-2.5 rounded-sm",
                "eh-docreport-aging__segment",
                `eh-docreport-aging__segment--${bucket}`,
              )}
              aria-hidden
            />
            <span>{OPEN_INVOICE_BUCKET_LABELS[bucket]}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function OffenePostenPage() {
  const { user } = useCurrentUser();
  const isBuero = userIsBuero(user);
  const { open: openDocumentEditor } = useDocumentEditor();
  const [report, setReport] = useState<OffenePostenReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [dueFilter, setDueFilter] = useState<DueFilter>("alle");
  const [detailView, setDetailView] = useState<DetailView>("invoices");
  const [busyId, setBusyId] = useState<string | null>(null);
  const [paymentBusyId, setPaymentBusyId] = useState<string | null>(null);
  const [paymentsDoc, setPaymentsDoc] = useState<DokumentDetail | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchOffenePosten();
      setReport(data);
      setError(null);
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : "Offene Posten konnten nicht geladen werden.",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const filteredItems = useMemo(() => {
    const term = search.trim().toLowerCase();
    let rows = report?.items ?? [];
    if (dueFilter === "ueberfaellig") {
      rows = rows.filter((row) => (row.days_overdue ?? 0) > 0);
    } else if (dueFilter === "nicht_faellig") {
      rows = rows.filter((row) => row.bucket === "not_due");
    } else if (dueFilter === "ohne_faelligkeit") {
      rows = rows.filter((row) => row.bucket === "no_due_date");
    } else if (dueFilter === "teilbezahlt") {
      rows = rows.filter((row) => row.zahlungsstatus === "teilbezahlt");
    }
    if (term) {
      rows = rows.filter((row) =>
        [
          row.dokument_nummer,
          row.kunde,
          row.auftragsnummer,
          row.beschreibung,
          row.betreff,
          row.dokument_typ_label,
        ]
          .map((value) => String(value || "").toLowerCase())
          .join(" ")
          .includes(term),
      );
    }
    return rows;
  }, [dueFilter, report?.items, search]);

  const filteredTotals = useMemo(() => {
    return filteredItems.reduce(
      (acc, row) => {
        acc.brutto += row.summe_brutto;
        acc.bezahlt += row.summe_bezahlt;
        acc.offen += row.summe_offen;
        return acc;
      },
      { brutto: 0, bezahlt: 0, offen: 0 },
    );
  }, [filteredItems]);

  const filteredCustomers = useMemo(() => {
    const term = search.trim().toLowerCase();
    let rows = report?.by_customer ?? [];
    if (term) {
      rows = rows.filter((row) => row.kunde.toLowerCase().includes(term));
    }
    return rows;
  }, [report?.by_customer, search]);

  const openItem = useCallback(
    async (row: OffenerPostenItem) => {
      if (busyId) return;
      if (row.dokument_id) {
        openDocumentEditor({
          docId: row.dokument_id,
          onClosed: () => {
            void load();
          },
        });
        return;
      }
      const heroId = row.hero_document_id;
      if (!heroId || heroId <= 0) {
        toast.error("Rechnung ohne Dokument-ID.");
        return;
      }
      setBusyId(row.id);
      try {
        const activated = await activateHeroDokument(heroId);
        openDocumentEditor({
          docId: activated.id,
          onClosed: () => {
            void load();
          },
        });
        void load();
      } catch (err) {
        toast.error(
          err instanceof ApiError ? err.message : "Hero-Rechnung konnte nicht geöffnet werden.",
        );
      } finally {
        setBusyId(null);
      }
    },
    [busyId, load, openDocumentEditor],
  );

  const capturePayment = useCallback(
    async (row: OffenerPostenItem) => {
      if (!canCaptureOffenerPostenZahlung(row) || paymentBusyId != null) return;
      setPaymentBusyId(row.id);
      try {
        let detail: DokumentDetail;
        if (row.dokument_id) {
          detail = await getDokument(row.dokument_id);
        } else {
          const heroId = row.hero_document_id;
          if (!heroId || heroId <= 0) {
            toast.error("Hero-Rechnung ohne ID.");
            return;
          }
          detail = await activateHeroDokument(heroId);
          void load();
        }
        setPaymentsDoc(detail);
      } catch (err) {
        toast.error(
          err instanceof ApiError ? err.message : "Rechnung konnte nicht geladen werden.",
        );
      } finally {
        setPaymentBusyId(null);
      }
    },
    [load, paymentBusyId],
  );

  const itemColumns = useMemo<Column<OffenerPostenItem>[]>(
    () => [
      {
        key: "dokument_nummer",
        header: "Rechnung",
        sortable: true,
        sortValue: (row) => row.dokument_nummer || "",
        render: (row) => (
          <div>
            <button
              type="button"
              className="text-left font-medium text-accent hover:underline"
              onClick={() => void openItem(row)}
              disabled={busyId === row.id}
            >
              {row.dokument_nummer || "–"}
            </button>
            <div className="mt-0.5 flex flex-wrap items-center gap-1.5 text-xs text-label-secondary">
              <span>{row.dokument_typ_label}</span>
              <span
                className={clsx(
                  "rounded-full px-1.5 py-0.5 text-[11px] font-medium",
                  row.quelle === "hero"
                    ? "bg-fill-tertiary text-label-secondary"
                    : "bg-accent/10 text-accent",
                )}
              >
                {row.quelle === "hero" ? "Hero" : "App"}
              </span>
            </div>
          </div>
        ),
      },
      {
        key: "kunde",
        header: "Kunde / Projekt",
        sortable: true,
        sortValue: (row) => row.kunde,
        render: (row) => (
          <div>
            <div>{row.kunde}</div>
            {row.auftrag_id ? (
              <ProjectOverviewLink
                auftragId={row.auftrag_id}
                label={[row.auftragsnummer, row.beschreibung || row.betreff]
                  .filter(Boolean)
                  .join(" – ")}
              />
            ) : row.betreff ? (
              <div className="text-xs text-label-secondary">{row.betreff}</div>
            ) : null}
          </div>
        ),
      },
      {
        key: "status",
        header: "Status",
        sortable: true,
        sortValue: (row) => row.zahlungsstatus,
        render: (row) => (
          <div>
            <div>{ZAHLUNGSSTATUS_LABELS[row.zahlungsstatus] || row.status_label}</div>
            <div className="text-xs text-label-secondary">{row.status_label}</div>
          </div>
        ),
      },
      {
        key: "belegdatum",
        header: "Belegdatum",
        sortable: true,
        sortValue: (row) => row.belegdatum || "",
        render: (row) => <span className="tabular-nums">{formatDateShortDe(row.belegdatum)}</span>,
      },
      {
        key: "faelligkeitsdatum",
        header: "Fällig am",
        sortable: true,
        sortValue: (row) => row.faelligkeitsdatum || "",
        render: (row) => (
          <span className="tabular-nums">{formatDateShortDe(row.faelligkeitsdatum)}</span>
        ),
      },
      {
        key: "days_overdue",
        header: "Überfällig",
        sortable: true,
        sortValue: (row) => row.days_overdue ?? -9999,
        render: (row) =>
          row.days_overdue != null && row.days_overdue > 0 ? (
            <span className={clsx("tabular-nums font-medium", overdueClass(row.bucket))}>
              {row.days_overdue} Tage
            </span>
          ) : (
            <span className="text-label-tertiary">–</span>
          ),
      },
      {
        key: "mahnstufe",
        header: "Mahnung",
        sortable: true,
        sortValue: (row) => row.mahnstufe || "",
        render: (row) => {
          if (!row.mahnstufe) {
            return <span className="text-label-tertiary">–</span>;
          }
          const label =
            row.mahnstufe_label ||
            MAHNSTUFE_LABELS[row.mahnstufe as keyof typeof MAHNSTUFE_LABELS] ||
            row.mahnstufe;
          if (row.mahnstufe === "keine" && !row.mahnung_pausiert) {
            return <span className="text-label-tertiary">–</span>;
          }
          return (
            <div>
              <div>{label === "Keine" ? "–" : label}</div>
              {row.mahnung_pausiert ? (
                <div className="text-xs text-status-orange">Pausiert</div>
              ) : null}
            </div>
          );
        },
      },
      {
        key: "summe_brutto",
        header: "Brutto",
        className: "text-right",
        headerClassName: "text-right",
        sortable: true,
        sortValue: (row) => row.summe_brutto,
        render: (row) => <span className="tabular-nums">{formatEuroDe(row.summe_brutto)}</span>,
      },
      {
        key: "summe_bezahlt",
        header: "Bezahlt",
        className: "text-right",
        headerClassName: "text-right",
        sortable: true,
        sortValue: (row) => row.summe_bezahlt,
        render: (row) => <span className="tabular-nums">{formatEuroDe(row.summe_bezahlt)}</span>,
      },
      {
        key: "summe_offen",
        header: "Offen",
        className: "text-right",
        headerClassName: "text-right",
        sortable: true,
        sortValue: (row) => row.summe_offen,
        render: (row) => (
          <span className="eh-docreport-amount--open tabular-nums font-semibold">
            {formatEuroDe(row.summe_offen)}
          </span>
        ),
      },
      {
        key: "actions",
        header: "",
        headerClassName: "text-right",
        className: "text-right whitespace-nowrap",
        render: (row) =>
          canCaptureOffenerPostenZahlung(row) ? (
            <Button
              type="button"
              size="sm"
              variant="secondary"
              loading={paymentBusyId === row.id}
              disabled={paymentBusyId != null || busyId != null}
              onClick={(e) => {
                e.stopPropagation();
                void capturePayment(row);
              }}
            >
              {row.zahlungsstatus === "teilbezahlt" ? "Teilzahlung" : "Zahlung"}
            </Button>
          ) : null,
      },
    ],
    [busyId, capturePayment, openItem, paymentBusyId],
  );

  const customerColumns = useMemo<Column<OpenInvoiceCustomer>[]>(
    () => [
      { key: "kunde", header: "Kunde", render: (row) => row.kunde },
      {
        key: "open_count",
        header: "Offene Rechnungen",
        render: (row) => <span className="tabular-nums">{row.open_count}</span>,
      },
      {
        key: "open_amount",
        header: "Offener Betrag",
        render: (row) => (
          <span className="eh-docreport-amount--open tabular-nums font-medium">
            {formatEuroDe(row.open_amount)}
          </span>
        ),
      },
      {
        key: "max_days_overdue",
        header: "Max. überfällig",
        render: (row) =>
          row.max_days_overdue > 0 ? (
            <span className="tabular-nums">{row.max_days_overdue} Tage</span>
          ) : (
            <span className="text-label-tertiary">–</span>
          ),
      },
    ],
    [],
  );

  const summary = report?.summary;
  const dueFilterLabel =
    DUE_FILTERS.find((opt) => opt.value === dueFilter)?.label ?? "Alle offenen";
  const overviewSummary = summary
    ? `${formatEuroCompact(summary.open_amount)} offen · ${formatEuroCompact(summary.overdue_amount)} überfällig · ${summary.open_count ?? 0} Rechnungen`
    : undefined;
  const filterSummary = search.trim() ? `${dueFilterLabel} · Suche aktiv` : dueFilterLabel;

  return (
    <AppShell
      title="Offene Posten"
      subtitle="Offene Ausgangsrechnungen mit Restsaldo, Fälligkeit und Altersstruktur."
      actions={
        <>
          {isBuero ? null : (
            <Link
              to="/dokumente"
              className="inline-flex items-center rounded-lg border border-[var(--eh-color-border)] px-3 py-2 text-sm font-medium text-label hover:bg-[var(--eh-color-surface-muted)]"
            >
              Zu Dokumenten
            </Link>
          )}
          <Link
            to="/dokumente/mahnwesen"
            className="inline-flex items-center rounded-lg border border-[var(--eh-color-border)] px-3 py-2 text-sm font-medium text-label hover:bg-[var(--eh-color-surface-muted)]"
          >
            Mahnwesen
          </Link>
          <Button variant="secondary" size="sm" onClick={() => void load()} loading={loading}>
            Neu laden
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {error ? (
          <Alert variant="error" title="Offene Posten konnten nicht geladen werden">
            {error}
          </Alert>
        ) : null}

        <CollapsibleSection
          sectionKey="dokumente.offene-posten.filter"
          title="Filter"
          summary={filterSummary}
          actions={
            dueFilter !== "alle" || search.trim() ? (
              <button
                type="button"
                onClick={() => {
                  setDueFilter("alle");
                  setSearch("");
                }}
                className="rounded-lg px-2 py-1 text-xs font-medium text-accent transition hover:bg-fill-tertiary"
              >
                Zurücksetzen
              </button>
            ) : null
          }
        >
          <section className="rounded-ios-lg bg-card px-4 py-4 shadow-ios-md ring-1 ring-separator/35">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
              <div className="w-full sm:w-56">
                <SelectInput
                  label="Filter"
                  value={dueFilter}
                  onChange={(e) => setDueFilter(e.target.value as DueFilter)}
                >
                  {DUE_FILTERS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </SelectInput>
              </div>
              <div className="w-full sm:w-72">
                <TextInput
                  label="Suche"
                  placeholder="Rechnung, Kunde oder Projekt …"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
            </div>
            <p className="mt-3 text-xs text-label-secondary">
              App-Rechnungen (RE26-xxxx) und Hero-Rechnungen mit Restsaldo. In Hero bereits bezahlte
              Belege erscheinen nicht.
              {report?.as_of ? `Stand: ${formatDateShortDe(report.as_of)}.` : ""}
              {report?.last_synced_at
                ? ` Letzter Hero-Sync: ${formatDateTimeDe(report.last_synced_at)}.`
                : ""}
            </p>
          </section>
        </CollapsibleSection>

        {loading && !report ? (
          <div className="flex items-center justify-center py-16 text-label-secondary">
            <Spinner />
            <span className="ml-2 text-sm">Offene Posten werden geladen …</span>
          </div>
        ) : report ? (
          <>
            <CollapsibleSection
              sectionKey="dokumente.offene-posten.uebersicht"
              title="Übersicht"
              summary={overviewSummary}
            >
              <div className="space-y-4">
                <section className="grid grid-cols-2 gap-3 lg:grid-cols-4">
                  <div className="rounded-ios-lg bg-card px-4 py-3 shadow-ios-md ring-1 ring-separator/35">
                    <p className="text-xs text-label-secondary">Offen gesamt</p>
                    <p className="eh-docreport-amount--open mt-1 text-2xl font-semibold tabular-nums">
                      {formatEuroCompact(summary?.open_amount)}
                    </p>
                    <p className="mt-1 text-[11px] text-label-tertiary">
                      {summary?.open_count ?? 0} Rechnungen
                    </p>
                  </div>
                  <div className="rounded-ios-lg bg-card px-4 py-3 shadow-ios-md ring-1 ring-separator/35">
                    <p className="text-xs text-label-secondary">Davon überfällig</p>
                    <p className="mt-1 text-2xl font-semibold tabular-nums text-label">
                      {formatEuroCompact(summary?.overdue_amount)}
                    </p>
                    <p className="mt-1 text-[11px] text-label-tertiary">
                      {summary?.overdue_count ?? 0} Rechnungen
                    </p>
                  </div>
                  <div className="rounded-ios-lg bg-card px-4 py-3 shadow-ios-md ring-1 ring-separator/35">
                    <p className="text-xs text-label-secondary">Noch nicht fällig</p>
                    <p className="mt-1 text-2xl font-semibold tabular-nums text-label">
                      {formatEuroCompact(summary?.not_due_amount)}
                    </p>
                    <p className="mt-1 text-[11px] text-label-tertiary">
                      {summary?.not_due_count ?? 0} Rechnungen
                    </p>
                  </div>
                  <div className="rounded-ios-lg bg-card px-4 py-3 shadow-ios-md ring-1 ring-separator/35">
                    <p className="text-xs text-label-secondary">Bereits bezahlt (Anteil)</p>
                    <p className="mt-1 text-2xl font-semibold tabular-nums text-label">
                      {formatEuroCompact(summary?.bezahlt_amount)}
                    </p>
                    <p className="mt-1 text-[11px] text-label-tertiary">
                      von {formatEuroCompact(summary?.brutto_amount)} Brutto
                    </p>
                  </div>
                </section>

                <section className="rounded-ios-lg bg-card px-4 py-4 shadow-ios-md ring-1 ring-separator/35">
                  <p className="mb-2 text-xs text-label-secondary">Altersstruktur</p>
                  <AgingBar buckets={report.summary.buckets} total={report.summary.open_amount} />
                  <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-5">
                    {OPEN_INVOICE_BUCKETS.map((bucket) => {
                      const entry = report.summary.buckets[bucket] ?? { count: 0, amount: 0 };
                      return (
                        <div
                          key={bucket}
                          className={clsx(
                            "rounded-ios-lg px-4 py-3 ring-1 ring-separator/35",
                            "eh-docreport-aging-card",
                            `eh-docreport-aging-card--${bucket}`,
                          )}
                        >
                          <p className="text-xs font-medium text-label-secondary">
                            {OPEN_INVOICE_BUCKET_LABELS[bucket]}
                          </p>
                          <p className="mt-1 text-lg font-semibold tabular-nums text-label">
                            {formatEuroCompact(entry.amount)}
                          </p>
                          <p className="mt-1 text-[11px] text-label-tertiary">
                            {entry.count} Rechnungen
                          </p>
                        </div>
                      );
                    })}
                  </div>
                </section>
              </div>
            </CollapsibleSection>

            <section className="flex flex-wrap items-center justify-between gap-3">
              <div className="inline-flex rounded-lg bg-fill-tertiary p-1">
                <button
                  type="button"
                  className={clsx(
                    "rounded-md px-3 py-1.5 text-sm transition",
                    detailView === "invoices"
                      ? "bg-accent text-white"
                      : "text-label-secondary hover:text-label",
                  )}
                  onClick={() => setDetailView("invoices")}
                >
                  Rechnungen
                </button>
                <button
                  type="button"
                  className={clsx(
                    "rounded-md px-3 py-1.5 text-sm transition",
                    detailView === "customers"
                      ? "bg-accent text-white"
                      : "text-label-secondary hover:text-label",
                  )}
                  onClick={() => setDetailView("customers")}
                >
                  Nach Kunde
                </button>
              </div>
              <span className="text-sm text-label-secondary">
                {detailView === "invoices"
                  ? `${filteredItems.length} Rechnungen · Offen ${formatEuroDe(filteredTotals.offen)}`
                  : `${filteredCustomers.length} Kunden`}
              </span>
            </section>

            {detailView === "invoices" ? (
              <Table
                columns={itemColumns}
                rows={filteredItems}
                rowKey={(row) => row.id}
                emptyText="Keine offenen Rechnungen gefunden."
                stickyHeader
                stickyEndColumnCount={1}
                rowClassName={(row) =>
                  row.bucket === "d61_plus" ? "eh-docreport-row--severe" : ""
                }
              />
            ) : (
              <Table
                columns={customerColumns}
                rows={filteredCustomers}
                rowKey={(row) => `${row.kunden_id ?? row.kunde}`}
                emptyText="Keine offenen Rechnungen gefunden."
                stickyHeader
              />
            )}
          </>
        ) : null}
      </div>

      {paymentsDoc ? (
        <DocumentPaymentsDialog
          open
          doc={paymentsDoc}
          onClose={() => setPaymentsDoc(null)}
          onUpdated={(next) => {
            setPaymentsDoc(next);
            void load();
          }}
        />
      ) : null}
    </AppShell>
  );
}
